import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { filter, startWith, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

import { BaseComponentOnDestroy } from '@common/epics/base-component-on-destroy';
import { ToTypedFormGroup } from '@common/model/to-typed-form-group.model';
import { FormGroup as LegacyFormGroup } from '@common/ngx-strongly-typed-forms';
import { PhxConstants } from '@common/PhoenixCommon.module';

import { IPaymentPartiesRateDetail, IPaymentRate, IPaymentRatesDetail, IReadOnlyStorage, ITabTimeMaterialInvoiceDetail, OvertimeDetails } from '../../models';
import { PartyPaymentInfoFormService, TimeMaterialInvoiceTabFormService, WorkorderService } from '../../services';


type PhxFormGroup<T> = FormGroup<ToTypedFormGroup<T>> | LegacyFormGroup<T>;

@Component({
  selector: 'app-workorder-billing-rate-overtime-config',
  templateUrl: './workorder-billing-rate-overtime-config.component.html',
  styleUrls: ['./workorder-billing-rate-overtime-config.component.less'],
  standalone: false
})
export class WorkorderBillingRateOvertimeConfigComponent extends BaseComponentOnDestroy implements OnInit {
  @Input() readOnlyStorage: IReadOnlyStorage;

  isElegibleForAutoCalculateOvertime = false;
  overtimeDetailsFormGroup: PhxFormGroup<OvertimeDetails>;

  constructor(
    private partyPaymentInfoFormService: PartyPaymentInfoFormService,
    private timeMaterialInvoiceTabFormService: TimeMaterialInvoiceTabFormService,
    private workorderService: WorkorderService
  ) {
    super();
  }

  ngOnInit(): void {
    this.overtimeDetailsFormGroup = this.partyPaymentInfoFormService.formGroup.controls.OverTimeDetails as PhxFormGroup<OvertimeDetails>;

    const paymentRateDetailsArray = this.partyPaymentInfoFormService.paymentPartiesRateDetailFormArray.at(0) as unknown as FormGroup<ToTypedFormGroup<IPaymentPartiesRateDetail>>;
    const paymentRateDetailsFormGroup = paymentRateDetailsArray.controls.PaymentRatesDetail as unknown as FormGroup<ToTypedFormGroup<IPaymentRatesDetail>>;
    const paymentRatesArray = paymentRateDetailsFormGroup.controls.PaymentRates as unknown as FormArray<FormGroup<ToTypedFormGroup<IPaymentRate>>>;
    const paymentRateFormGroup = paymentRatesArray.at(0) as unknown as FormGroup<ToTypedFormGroup<IPaymentRate>>;
    const paymentRate$ = paymentRateFormGroup.valueChanges;

    const timeInvoiceDetailsForm = this.timeMaterialInvoiceTabFormService.formGroup.controls.TabTimeMaterialInvoiceDetail as unknown as FormGroup<ToTypedFormGroup<ITabTimeMaterialInvoiceDetail>>;
    const timeSheetMethodology$ = timeInvoiceDetailsForm.controls.TimeSheetMethodologyId.valueChanges.pipe(startWith(timeInvoiceDetailsForm.controls.TimeSheetMethodologyId.value));
    const usesProjects$ = timeInvoiceDetailsForm.controls.IsTimeSheetUsesProjects.valueChanges.pipe(startWith(timeInvoiceDetailsForm.controls.IsTimeSheetUsesProjects.value));

    /** NOTE: this subscription is to set the isElegibleForAutoCalculateOvertime when the work order is loaded */
    paymentRate$.pipe(
      startWith(paymentRateFormGroup.value),
      filter(paymentRate => !!paymentRate),
      take(1)
    ).subscribe(paymentRate => {
      this.isElegibleForAutoCalculateOvertime = this.checkIsElegibleForAutoCalculateOvertime(paymentRate);
    });

    paymentRate$.pipe(
      takeUntil(this.isDestroyed$),
      tap(paymentRate => this.isElegibleForAutoCalculateOvertime = this.checkIsElegibleForAutoCalculateOvertime(paymentRate)),
      withLatestFrom(timeSheetMethodology$, usesProjects$)
    ).subscribe(([paymentRate, timeSheetMethodology, usesProjects]) => {
      if (!this.isElegibleForAutoCalculateOvertime) {
        this.overtimeDetailsFormGroup.controls.AutoCalculateOvertime.patchValue(false, { emitEvent: false });
        this.disableOvertimeExemptionReason();
        /** NOTE: has hourly rate, is not projects, ts methodology is online or offline */
      } else if (this.isStateValidToAutoCalculateOvertime(paymentRate, timeSheetMethodology, usesProjects)) {
        /** NOTE: default autocalculate overtime to true if not set  */
        if (this.overtimeDetailsFormGroup.controls.AutoCalculateOvertime.value === null) {
          this.overtimeDetailsFormGroup.controls.AutoCalculateOvertime.patchValue(true, { emitEvent: false });
          this.disableOvertimeExemptionReason();
        } else if (this.overtimeDetailsFormGroup.controls.AutoCalculateOvertime.value === false) {
          this.overtimeDetailsFormGroup.controls.AutoCalculateOvertime.enable({ emitEvent: false });
          this.enableOvertimeExemptionReason();
        } else {
          this.disableOvertimeExemptionReason();
        }
      } else {
        this.overtimeDetailsFormGroup.controls.AutoCalculateOvertime.patchValue(null, { emitEvent: false });
        this.overtimeDetailsFormGroup.controls.AutoCalculateOvertimeExemptionReason.disable({ emitEvent: false });
        this.overtimeDetailsFormGroup.controls.AutoCalculateOvertimeExemptionReason.patchValue(null, { emitEvent: false });
      }
    });

    this.overtimeDetailsFormGroup.controls.IsOvertimeExempt.valueChanges.pipe(
      takeUntil(this.isDestroyed$),
      filter(() => this.readOnlyStorage.IsEditable),
      withLatestFrom(
        paymentRate$.pipe(startWith(paymentRateFormGroup.value)),
        timeSheetMethodology$,
        usesProjects$),
    ).subscribe(([isOvertimeExempt, paymentRate, timeSheetMethodology, usesProjects]) => {
      if (isOvertimeExempt) {
        this.overtimeDetailsFormGroup.controls.OvertimeExemptionReason.patchValue(null, { emitEvent: false });
        this.overtimeDetailsFormGroup.controls.OvertimeExemptionReason.enable({ emitEvent: false });
        this.overtimeDetailsFormGroup.controls.AutoCalculateOvertime.patchValue(false, { emitEvent: false });
        this.disableOvertimeExemptionReason();
      } else {
        this.overtimeDetailsFormGroup.controls.OvertimeExemptionReason.patchValue(null, { emitEvent: false });
        this.overtimeDetailsFormGroup.controls.OvertimeExemptionReason.disable({ emitEvent: false });
        if (this.isStateValidToAutoCalculateOvertime(paymentRate, timeSheetMethodology, usesProjects)) {
          this.overtimeDetailsFormGroup.controls.AutoCalculateOvertime.enable({ emitEvent: false });
          this.overtimeDetailsFormGroup.controls.AutoCalculateOvertime.patchValue(true, { emitEvent: false });
        }
      }
    });

    this.overtimeDetailsFormGroup.controls.AutoCalculateOvertime.valueChanges.pipe(
      takeUntil(this.isDestroyed$),
      filter(() => this.readOnlyStorage.IsEditable),
    ).subscribe(autoCalculateOvertime => {
      if (autoCalculateOvertime) {
        this.disableOvertimeExemptionReason();
      } else {
        this.enableOvertimeExemptionReason();
      }
    });
  }

  private disableOvertimeExemptionReason() {
    this.overtimeDetailsFormGroup.controls.AutoCalculateOvertimeExemptionReason.patchValue(null, { emitEvent: false });
    this.overtimeDetailsFormGroup.controls.AutoCalculateOvertimeExemptionReason.disable({ emitEvent: false });
  }

  private enableOvertimeExemptionReason() {
    this.overtimeDetailsFormGroup.controls.AutoCalculateOvertimeExemptionReason.patchValue(null, { emitEvent: false });
    this.overtimeDetailsFormGroup.controls.AutoCalculateOvertimeExemptionReason.enable({ emitEvent: false });
  }

  private checkIsElegibleForAutoCalculateOvertime(paymentRate: IPaymentRate): boolean {
    return this.partyPaymentInfoFormService.isOvertimeEligibleProfile
      && this.workorderService.autoCalculateOvertimeIsActiveForWorkorder
      && this.partyPaymentInfoFormService.isOvertimeEligibleRates([paymentRate]);
  }

  private isStateValidToAutoCalculateOvertime(paymentRate: IPaymentRate, timeSheetMethodology: number, usesProjects: boolean): boolean {
    return this.checkIsElegibleForAutoCalculateOvertime(paymentRate)
      && !usesProjects
      && (timeSheetMethodology === PhxConstants.TimeSheetMethodology.OnlineApproval ||
        timeSheetMethodology === PhxConstants.TimeSheetMethodology.OfflineApproval)
      && this.overtimeDetailsFormGroup.controls.IsOvertimeExempt.value === false;
  }
}
